<template>
  <div
    :id="`alert-item-${id}-container`"
    :data-testid="`alert-item-${id}-container`"
    :class="[
      'flex min-w-96 max-w-full items-start justify-between rounded-md border px-3 py-2',
      { 'shadow-md': shadow },
      { 'border-neutral-300 bg-neutral-100': type === 'neutral' },
      { 'border-secondary-200 bg-secondary-100': type === 'secondary' },
      { 'border-positive-200 bg-positive-100': type === 'positive' },
      { 'border-warning-200 bg-warning-100': type === 'warning' },
      { 'border-negative-200 bg-negative-100': type === 'error' },
    ]"
  >
    <div class="flex flex-row justify-start">
      <component
        :is="getIcon()"
        v-if="icon"
        :class="[
          'mt-0.5 size-5',
          { 'fill-neutral-700': type === 'neutral' },
          { 'fill-secondary-700': type === 'secondary' },
          { 'fill-positive-700': type === 'positive' },
          { 'fill-warning-700': type === 'warning' },
          { 'fill-negative-700': type === 'error' },
        ]"
      />
      <div class="ml-2 flex flex-col">
        <UiHeading v-if="title" type="h5" class="font-medium text-neutral-900">
          {{ $t(title) }}
        </UiHeading>
        <UiText v-if="text" class="text-neutral-900">
          {{ translate ? $t(text) : text }}
        </UiText>
      </div>
    </div>
    <div class="flex flex-row justify-start">
      <SfButton
        v-if="actionLabel && actionClick"
        :id="`alert-item-${id}-action`"
        variant="tertiary"
        size="sm"
        class="p-1"
        :class="[
          { 'mr-2': closeable },
          {
            '!text-neutral-700 hover:!bg-neutral-200 hover:text-neutral-800 focus:!bg-neutral-300 focus:text-neutral-900':
              type === 'neutral',
          },
          {
            '!text-secondary-700 hover:!bg-secondary-200 hover:text-secondary-800 focus:!bg-secondary-300 focus:text-secondary-900':
              type === 'secondary',
          },
          {
            '!text-positive-700 hover:!bg-positive-200 hover:text-positive-800 focus:!bg-positive-300 focus:text-positive-900':
              type === 'positive',
          },
          {
            '!text-warning-700 hover:!bg-warning-200 hover:text-warning-800 focus:!bg-warning-300 focus:text-warning-900':
              type === 'warning',
          },
          {
            '!text-negative-700 hover:!bg-negative-200 hover:text-negative-800 focus:!bg-negative-300 focus:text-negative-900':
              type === 'error',
          },
        ]"
        @click="actionClick()"
      >
        {{ $t(actionLabel) }}
      </SfButton>
      <UiIconButton
        v-if="closeable"
        :id="`alert-item-${id}-close`"
        :icon="IconClose"
        :icon-class="[
          { '!fill-neutral-700 hover:!fill-neutral-800 focus:!fill-neutral-900': type === 'neutral' },
          { '!fill-secondary-700 hover:!fill-secondary-800 focus:!fill-secondary-900': type === 'secondary' },
          { '!fill-positive-700 hover:!fill-positive-800 focus:!fill-positive-900': type === 'positive' },
          { '!fill-warning-700 hover:!fill-warning-800 focus:!fill-warning-900': type === 'warning' },
          { '!fill-negative-700 hover:!fill-negative-800 focus:!fill-negative-900': type === 'error' },
        ]"
        :class="[
          { 'hover:!bg-neutral-200 focus:!bg-neutral-700': type === 'neutral' },
          { 'hover:!bg-secondary-200 focus:!bg-secondary-700': type === 'secondary' },
          { 'hover:!bg-positive-200 focus:!bg-positive-700': type === 'positive' },
          { 'hover:!bg-warning-200 focus:!bg-warning-700': type === 'warning' },
          { 'hover:!bg-negative-200 focus:!bg-negative-700': type === 'error' },
        ]"
        @click="emit('close')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconCheckCircle, IconClose, IconError, IconInfo, IconWarning } from '#components';
import { useTimeoutFn } from '@vueuse/core';

import type { Alert } from '~/stores/alert/types';

const DEFAULT_TIMEOUT = 5000;

const props = withDefaults(defineProps<Alert>(), {
  closeable: true,
  icon: true,
  timeout: DEFAULT_TIMEOUT,
  translate: true,
  type: 'neutral',
});

const getIcon = (): Component | undefined => {
  switch (props.type) {
    case 'error':
      return IconError;
    case 'neutral':
      return IconInfo;
    case 'positive':
      return IconCheckCircle;
    case 'secondary':
      return IconInfo;
    case 'warning':
      return IconWarning;
  }
};

const emit = defineEmits<{
  close: [];
}>();

useTimeoutFn(() => {
  emit('close');
}, props.timeout);
</script>
